/**
 * La roue du jour (migration 0738) : dix cases, une dorée. Le tirage se fait sur le serveur
 * (`spin_trousse_wheel`) ; la roue ne fait que montrer le résultat — elle tourne puis s'arrête sur la case
 * dorée si c'est gagné, sur une autre sinon (`wheelStopAngle`). Sans animation si l'appareil la refuse.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, usePrefersReducedMotion } from '@jeraisonne/ui';
import { TrousseShowcase } from '@/features/trousse/trousse-showcase';
import {
  goldSlices,
  nextSpinLabel,
  serialLabel,
  trousseGiftErrorMessage,
  trousseRarity,
  wheelStopAngle,
  winChanceLabel,
  WHEEL_SLICES,
  type SpinResult,
} from '@/lib/trousses';

const SPIN_MS = 3600;

/** Le dessin de la roue : dix parts, les `gold` premières (en haut au repos) sont dorées. */
export function WheelDisc({ size = 240, gold = 1 }: { size?: number; gold?: number }) {
  const r = 50;
  const parts = Array.from({ length: WHEEL_SLICES }, (_, i) => {
    const a0 = ((i - 0.5) * 360) / WHEEL_SLICES - 90;
    const a1 = ((i + 0.5) * 360) / WHEEL_SLICES - 90;
    const p = (a: number) => {
      const rad = (a * Math.PI) / 180;
      return `${50 + r * Math.cos(rad)} ${50 + r * Math.sin(rad)}`;
    };
    return { i, d: `M50 50 L${p(a0)} A${r} ${r} 0 0 1 ${p(a1)} Z` };
  });
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
      {parts.map(({ i, d }) => (
        <path
          key={i}
          d={d}
          fill={i < gold ? '#f5b82e' : i % 2 ? '#1d3a7a' : '#2b56a8'}
          stroke="#061433"
          strokeWidth={0.6}
        />
      ))}
      {/* l'étoile de la case dorée */}
      <text x="50" y="13" textAnchor="middle" fontSize="8" fill="#061433">
        ★
      </text>
      <circle cx="50" cy="50" r="7" fill="#061433" stroke="#f5b82e" strokeWidth="1.2" />
    </svg>
  );
}

export function WheelDialog({
  winChance,
  onSpin,
  onClose,
  onEquip,
  onCollection,
}: {
  winChance: number;
  onSpin: () => Promise<SpinResult>;
  onClose: () => void;
  /** porter tout de suite la trousse gagnée */
  onEquip: (slug: string) => void;
  /** « Voir ma collection » quand la roue n'est pas déjà ouverte depuis la collection */
  onCollection?: () => void;
}) {
  const reduced = usePrefersReducedMotion();
  const gold = goldSlices(winChance);
  const [angle, setAngle] = useState(0);
  const [phase, setPhase] = useState<'ready' | 'spinning' | 'done'>('ready');
  const [result, setResult] = useState<SpinResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  // la page se redessine pendant que la roue tourne (la collection se recharge) et passe un nouveau
  // `onClose` : il ne doit jamais relancer l'effet, sinon l'arrêt de la roue était annulé
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    button.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // l'arrêt de la roue ne s'annule qu'en quittant la fenêtre
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  async function spin() {
    setError(null);
    setPhase('spinning');
    let r: SpinResult;
    try {
      r = await onSpin();
    } catch (e) {
      setError(trousseGiftErrorMessage((e as Error).message));
      setPhase('ready');
      return;
    }
    setResult(r);
    setAngle(wheelStopAngle(r.won, Math.random(), { turns: reduced ? 0 : 5, gold }));
    timer.current = window.setTimeout(() => setPhase('done'), reduced ? 0 : SPIN_MS);
  }

  const t = result?.trousse ?? null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-[rgba(6,20,51,0.7)] p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wheel-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && phase !== 'spinning') onClose();
      }}
    >
      <div className="ocean:bg-[#061433] w-full max-w-md rounded-t-3xl bg-white px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 text-center shadow-xl sm:rounded-3xl">
        <h2 id="wheel-title" className="font-display text-ink-900 text-xl font-extrabold">
          La roue du jour
        </h2>
        <p className="text-ink-500 mt-1 text-sm">
          Aujourd’hui : {winChanceLabel(winChance)} de gagner une trousse. Chaque jour perdu
          d’affilée t’en donne une de plus.
        </p>

        <div className="relative mx-auto mt-5 h-60 w-60">
          {/* le pointeur, en haut */}
          <span
            className="absolute left-1/2 top-[-6px] z-10 h-0 w-0 -translate-x-1/2 border-x-[10px] border-t-[18px] border-x-transparent border-t-[#f5b82e] drop-shadow"
            aria-hidden
          />
          <div
            data-testid="wheel-disc"
            style={{
              transform: `rotate(${angle}deg)`,
              transition:
                reduced || !angle
                  ? 'none'
                  : `transform ${SPIN_MS}ms cubic-bezier(0.17, 0.67, 0.21, 1)`,
            }}
          >
            <WheelDisc gold={gold} />
          </div>
        </div>

        <div className="mt-5 min-h-[7rem]" aria-live="polite">
          {phase === 'done' && result ? (
            result.won && t ? (
              <div className="space-y-3">
                <TrousseShowcase trousse={t} celebrate />
                <p className="text-ink-900 font-bold">
                  {t.count > 1
                    ? `Gagné ! Encore une « ${t.name} » : tu en as ${t.count}.`
                    : `Gagné ! La trousse « ${t.name} » est à toi.`}
                </p>
                <p className="text-ink-500 text-xs">
                  {trousseRarity(t.copies)} · {serialLabel(t.serial, t.copies)}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button fullWidth onClick={() => onEquip(t.slug)}>
                    La porter
                  </Button>
                  <Button fullWidth variant="outline" onClick={onCollection ?? onClose}>
                    Voir ma collection
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-ink-900 font-bold">
                  {result.nothing_left
                    ? 'Il ne reste plus de trousse à gagner !'
                    : 'Pas cette fois…'}
                </p>
                <p className="text-ink-500 text-sm">
                  Reviens {nextSpinLabel(result.next_spin_at)} pour tourner à nouveau.
                </p>
                <Button fullWidth variant="outline" onClick={onClose}>
                  Fermer
                </Button>
              </div>
            )
          ) : (
            <div className="space-y-3">
              <Button
                ref={button}
                fullWidth
                size="lg"
                loading={phase === 'spinning'}
                disabled={phase === 'spinning'}
                onClick={() => void spin()}
              >
                {phase === 'spinning' ? 'La roue tourne…' : 'Tourner la roue'}
              </Button>
              {error && (
                <p role="alert" className="text-error-600 text-sm">
                  {error}
                </p>
              )}
              {phase === 'ready' && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-ink-500 min-h-11 text-sm font-semibold"
                >
                  Plus tard
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
